import type { Opportunity, IndicationType } from '@/types/opportunity';

export interface SessionStatistics {
  totalOpportunities: number;
  actionsPerformed: number;
  actionsMissed: number;
  complianceRate: number;
  byMethod: {
    handrub: number;
    handwash: number;
    both: number;
  };
  byHCWCategory: Array<{
    code: string;
    name: string;
    opportunities: number;
    compliance: number;
    healthworkerCount: number;
  }>;
  byIndication: Array<{
    indication: IndicationType;
    count: number;
    compliance: number;
  }>;
  glovesWornCount: number;
}

export function calculateStatistics(
  opportunities: Opportunity[]
): SessionStatistics {
  const total = opportunities.length;
  const performed = opportunities.filter((o) => o.action.actionPerformed);
  const missed = opportunities.filter((o) => !o.action.actionPerformed);

  // By method
  const handrub = performed.filter((o) => o.action.actionType === 'handrub').length;
  const handwash = performed.filter((o) => o.action.actionType === 'handwash').length;
  const both = performed.filter((o) => o.action.actionType === 'both').length;

  // By HCW category (with distinct healthworker count)
  const hcwMap = new Map<string, { name: string; total: number; performed: number; healthworkers: Set<string> }>();
  for (const opp of opportunities) {
    const existing = hcwMap.get(opp.observedHCWCode) ?? {
      name: opp.observedHCWName,
      total: 0,
      performed: 0,
      healthworkers: new Set<string>(),
    };
    existing.total++;
    if (opp.action.actionPerformed) existing.performed++;
    existing.healthworkers.add(opp.healthworkerIdentifier || 'star_1');
    hcwMap.set(opp.observedHCWCode, existing);
  }

  // By indication
  const indicationTypes: IndicationType[] = [
    'before_patient',
    'before_aseptic',
    'after_body_fluid',
    'after_patient',
    'after_surroundings',
  ];

  const byIndication = indicationTypes.map((indication) => {
    const matching = opportunities.filter((o) => o.indications.includes(indication));
    const matchPerformed = matching.filter((o) => o.action.actionPerformed).length;
    return {
      indication,
      count: matching.length,
      compliance: matching.length > 0 ? (matchPerformed / matching.length) * 100 : 0,
    };
  });

  // Gloves worn count (only for missed)
  const glovesWornCount = missed.filter((o) => o.action.glovesWorn === true).length;

  return {
    totalOpportunities: total,
    actionsPerformed: performed.length,
    actionsMissed: missed.length,
    complianceRate: total > 0 ? (performed.length / total) * 100 : 0,
    byMethod: { handrub, handwash, both },
    byHCWCategory: Array.from(hcwMap.entries()).map(([code, data]) => ({
      code,
      name: data.name,
      opportunities: data.total,
      compliance: data.total > 0 ? (data.performed / data.total) * 100 : 0,
      healthworkerCount: data.healthworkers.size,
    })),
    byIndication,
    glovesWornCount,
  };
}
